import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || "DEMO_MAP_ID";

// Renders the Daktylios polygon + radius circles for each landmark as imperative
// Google Maps overlays (the @vis.gl wrapper doesn't ship Polygon/Circle primitives).
const Overlays = ({ daktylios, landmarks }) => {
  const map = useMap();
  const overlaysRef = useRef([]);

  useEffect(() => {
    if (!map || !daktylios?.length) return;
    const g = window.google && window.google.maps;
    if (!g) return;
    // Clean up previous overlays before re-drawing.
    overlaysRef.current.forEach((o) => o.setMap(null));
    overlaysRef.current = [];

    const poly = new g.Polygon({
      paths: daktylios,
      strokeColor: "#e6b75c",
      strokeOpacity: 1,
      strokeWeight: 3,
      fillColor: "#e6b75c",
      fillOpacity: 0.18,
      clickable: false,
      map,
    });
    overlaysRef.current.push(poly);

    landmarks.forEach((lm) => {
      const circle = new g.Circle({
        center: { lat: lm.lat, lng: lm.lng },
        radius: lm.radius_km * 1000,
        strokeColor: "#34d399",
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: "#34d399",
        fillOpacity: 0.12,
        clickable: false,
        map,
      });
      overlaysRef.current.push(circle);
    });

    // Fit the camera to include the polygon plus all landmark circles.
    const bounds = new g.LatLngBounds();
    daktylios.forEach((p) => bounds.extend(p));
    landmarks.forEach((lm) =>
      bounds.extend({ lat: lm.lat, lng: lm.lng })
    );
    map.fitBounds(bounds, 60);

    return () => {
      overlaysRef.current.forEach((o) => o.setMap(null));
      overlaysRef.current = [];
    };
  }, [map, daktylios, landmarks]);

  return null;
};

const Daktylios = () => {
  const [geofence, setGeofence] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/geofence`)
      .then((r) => setGeofence(r.data))
      .catch((e) => setError(e?.message || "Failed to load geofence"));
  }, []);

  return (
    <main className="min-h-screen bg-[#0a1830] text-foreground">
      <div className="max-w-6xl mx-auto px-5 lg:px-10 pt-24 lg:pt-28 pb-10">
        <header className="mb-6">
          <div className="text-[0.7rem] uppercase tracking-[0.22em] text-gold">Admin · Pricing boundary</div>
          <h1 className="font-display text-3xl lg:text-4xl mt-1">Daktylios geofence map</h1>
          <p className="text-sm text-foreground/65 mt-2 max-w-2xl">
            Gold polygon = the central-Athens zone that triggers the advertised airport/port flat fares
            (€55 day / €70 night to/from the airport). Green circles = the landmark catchment radius for
            each airport/port. Any pickup or drop-off whose Google-resolved lat/lng falls inside both a
            landmark circle <em>and</em> the polygon gets the fixed price.
          </p>
        </header>
        {error && (
          <div className="rounded-xl bg-red-500/10 border border-red-400/30 px-4 py-3 text-sm text-red-200 mb-4">
            {error}
          </div>
        )}
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-black/30" style={{ height: "70vh" }}>
          {MAPS_KEY ? (
            <APIProvider apiKey={MAPS_KEY}>
              <Map
                mapId={MAP_ID}
                defaultCenter={{ lat: 37.98, lng: 23.73 }}
                defaultZoom={11}
                gestureHandling="greedy"
                disableDefaultUI={false}
              >
                {geofence && (
                  <>
                    <Overlays
                      daktylios={geofence.daktylios}
                      landmarks={geofence.landmarks}
                    />
                    {geofence.landmarks.map((lm) => (
                      <AdvancedMarker
                        key={lm.id}
                        position={{ lat: lm.lat, lng: lm.lng }}
                        title={lm.id}
                      />
                    ))}
                  </>
                )}
              </Map>
            </APIProvider>
          ) : (
            <div className="flex items-center justify-center h-full text-foreground/60 text-sm">
              REACT_APP_GOOGLE_MAPS_API_KEY is not set.
            </div>
          )}
        </div>

        {geofence && (
          <div className="mt-6 grid sm:grid-cols-2 gap-4 text-xs text-foreground/70">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="text-gold font-semibold mb-1">Polygon vertices ({geofence.daktylios.length})</div>
              <ul className="space-y-1 font-mono">
                {geofence.daktylios.map((p, i) => (
                  <li key={i}>{i + 1}. {p.lat.toFixed(4)}, {p.lng.toFixed(4)}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="text-gold font-semibold mb-1">Landmark anchors</div>
              <ul className="space-y-1 font-mono">
                {geofence.landmarks.map((lm) => (
                  <li key={lm.id}>
                    {lm.id.padEnd(8)} {lm.lat.toFixed(4)}, {lm.lng.toFixed(4)} · r={lm.radius_km} km
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default Daktylios;
